import { supabase } from '@/core/infrastructure/supabase/supabaseClient';

// ─── Tipos del Dominio Financiero ────────────────────────────────────────────

export interface Comprobante {
  comprobante_id: number;
  cita_id: string;
  banco_emisor: string;
  referencia_6_digitos: string;
  telefono_emisor: string;
  monto_pagado: number;
  status_pago: string;
  nota_profesional: string | null;
  fecha_subida: string;
  
  // JOINs
  fecha_hora_inicio: string;
  paciente_nombre: string;
  paciente_dni: string;
  paciente_fecha_nacimiento: string | null;
  paciente_telefono: string;
  paciente_avatar: string | null;
  servicio_titulo: string;
  profesional_uuid: string;
  paciente_uuid: string;
}

export type Decision = 'aprobado' | 'pago_parcial' | 'rechazado';

export interface DecisionResult {
  success: boolean;
  message?: string;
}

// ─── Repositorio ─────────────────────────────────────────────────────────────

/**
 * PaymentQueueRepository
 * 
 * Encapsula el acceso completo a Supabase para la cola de comprobantes de pago.
 * La UI no debe importar `supabase` directamente (DIP - Nota 12).
 */
export const PaymentQueueRepository = {

  /**
   * Obtiene todos los comprobantes de pago del profesional autenticado,
   * incluyendo JOINs con datos del paciente y servicio solicitado.
   */
  async fetchComprobantes(): Promise<Comprobante[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('cita_comprobantes_pago')
      .select(`
        comprobante_id, cita_id, banco_emisor, referencia_6_digitos,
        telefono_emisor, monto_pagado, status_pago, nota_profesional, fecha_subida,
        agendamiento_citas!inner (
          fecha_hora_inicio, profesional_uuid, paciente_uuid, estado,
          profesional_servicios_faq ( titulo_opcion ),
          perfiles_usuario!paciente_uuid ( 
            nombres_completos, 
            apellidos_completos,
            perfil_dni,
            perfil_fecha_nacimiento,
            telefono_personal,
            avatar_url
          )
        )
      `)
      .eq('agendamiento_citas.profesional_uuid', user.id)
      .in('agendamiento_citas.estado', ['pendiente_verificacion_pago', 'en_revision', 'pago_parcial', 'confirmada', 'completada'])
      .order('fecha_subida', { ascending: false });

    if (error || !data) return [];

    return (data as any[]).map(r => {
      const cita = r.agendamiento_citas;
      const paciente = cita?.perfiles_usuario;
      return {
        comprobante_id:       r.comprobante_id,
        cita_id:              r.cita_id,
        banco_emisor:         r.banco_emisor,
        referencia_6_digitos: r.referencia_6_digitos,
        telefono_emisor:      r.telefono_emisor,
        monto_pagado:         r.monto_pagado,
        status_pago:          r.status_pago,
        nota_profesional:     r.nota_profesional,
        fecha_subida:         r.fecha_subida,
        fecha_hora_inicio:    cita?.fecha_hora_inicio ?? '',
        paciente_nombre:      [paciente?.nombres_completos, paciente?.apellidos_completos].filter(Boolean).join(' ') || 'Paciente',
        paciente_dni:         paciente?.perfil_dni || 'No registrado',
        paciente_fecha_nacimiento: paciente?.perfil_fecha_nacimiento || null,
        paciente_telefono:    paciente?.telefono_personal || 'No registrado',
        paciente_avatar:      paciente?.avatar_url || null,
        servicio_titulo:      cita?.profesional_servicios_faq?.titulo_opcion || 'Consulta General',
        profesional_uuid:     cita?.profesional_uuid || '',
        paciente_uuid:        cita?.paciente_uuid || ''
      };
    });
  },

  /**
   * Obtiene un comprobante específico por ID de cita.
   */
  async fetchComprobanteById(citaId: string): Promise<Comprobante | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('cita_comprobantes_pago')
      .select(`
        comprobante_id, cita_id, banco_emisor, referencia_6_digitos,
        telefono_emisor, monto_pagado, status_pago, nota_profesional, fecha_subida,
        agendamiento_citas!inner (
          fecha_hora_inicio, profesional_uuid, paciente_uuid, estado,
          profesional_servicios_faq ( titulo_opcion ),
          perfiles_usuario!paciente_uuid ( 
            nombres_completos, 
            apellidos_completos,
            perfil_dni,
            perfil_fecha_nacimiento,
            telefono_personal,
            avatar_url
          )
        )
      `)
      .eq('cita_id', citaId)
      .eq('agendamiento_citas.profesional_uuid', user.id)
      .in('agendamiento_citas.estado', ['pendiente_verificacion_pago', 'en_revision', 'pago_parcial', 'confirmada', 'completada'])
      .single();

    if (error || !data) return null;

    const r = data as any;
    const cita = r.agendamiento_citas;
    const paciente = cita?.perfiles_usuario;
    
    return {
      comprobante_id:       r.comprobante_id,
      cita_id:              r.cita_id,
      banco_emisor:         r.banco_emisor,
      referencia_6_digitos: r.referencia_6_digitos,
      telefono_emisor:      r.telefono_emisor,
      monto_pagado:         r.monto_pagado,
      status_pago:          r.status_pago,
      nota_profesional:     r.nota_profesional,
      fecha_subida:         r.fecha_subida,
      fecha_hora_inicio:    cita?.fecha_hora_inicio ?? '',
      paciente_nombre:      [paciente?.nombres_completos, paciente?.apellidos_completos].filter(Boolean).join(' ') || 'Paciente',
      paciente_dni:         paciente?.perfil_dni || 'No registrado',
      paciente_fecha_nacimiento: paciente?.perfil_fecha_nacimiento || null,
      paciente_telefono:    paciente?.telefono_personal || 'No registrado',
      paciente_avatar:      paciente?.avatar_url || null,
      servicio_titulo:      cita?.profesional_servicios_faq?.titulo_opcion || 'Consulta General',
      profesional_uuid:     cita?.profesional_uuid || '',
      paciente_uuid:        cita?.paciente_uuid || ''
    };
  },

  /**
   * Ejecuta la decisión del profesional sobre un comprobante de pago
   * llamando al RPC blindado `fn_aprobar_comprobante_pago`.
   */
  async procesarDecision(citaId: string, decision: Decision, nota?: string): Promise<DecisionResult> {
    const { data, error } = await supabase.rpc('fn_aprobar_comprobante_pago', {
      p_cita_id:  citaId,
      p_decision: decision,
      p_nota:     nota ?? null,
    });

    if (error) {
      return { success: false, message: `Error de red o servidor: ${error.message}` };
    }
    
    if (data?.success === false) {
      return { success: false, message: `Operación denegada: ${data.message}` };
    }

    return { success: true };
  },

  /**
   * Congela la cita y la pasa al estado 'en_disputa'
   */
  async reportarDisputa(citaId: string, motivo: string): Promise<DecisionResult> {
    const { data, error } = await supabase.rpc('fn_reportar_disputa_cita', {
      p_cita_id: citaId,
      p_motivo: motivo,
    });

    if (error) {
      return { success: false, message: `Error de red o servidor: ${error.message}` };
    }
    
    if (data?.success === false) {
      return { success: false, message: `Operación denegada: ${data.message}` };
    }

    return { success: true };
  },
};
